import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, MessageSquareWarning, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Grievances() {
  const qc = useQueryClient();
  const { data: pending, isLoading } = useQuery({
    queryKey: ["grievancesPendingHR"],
    queryFn: async () => (await api.get("/grievances", { params: { status: "Pending_HR" } })).data,
  });
  const { data: all } = useQuery({ queryKey: ["grievancesAll"], queryFn: async () => (await api.get("/grievances")).data });

  const decide = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/grievances/${id}/decide`, { action, remarks: action === "approve" ? "Resolved" : "Rejected" })).data,
    onSuccess: () => { toast.success("Decision recorded"); qc.invalidateQueries({ queryKey: ["grievancesPendingHR"] }); qc.invalidateQueries({ queryKey: ["grievancesAll"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader overline="Employee Voice" title="Grievances" icon={MessageSquareWarning}
        subtitle="Grievances flow Employee → Reporting Officer (recommendation) → HR (final decision)." />
      <Tabs defaultValue="pending">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pending" data-testid="tab-grievance-pending">Pending Final Decision ({pending?.length || 0})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-grievance-all">All Grievances ({all?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          {isLoading ? <Loading /> : (pending || []).length === 0 ? (
            <EmptyState icon={MessageSquareWarning} title="No grievances awaiting HR decision" hint="Grievances recommended by a Reporting Officer (or with no RO in the chain) appear here." />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Category</TableHead>
                  <TableHead className="overline text-slate-500">Subject</TableHead>
                  <TableHead className="overline text-slate-500">Priority</TableHead>
                  <TableHead className="overline text-slate-500">RO Recommendation</TableHead>
                  <TableHead className="text-right overline text-slate-500">Decision</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {pending.map((g) => (
                    <TableRow key={g.id} className="table-row-i" data-testid={`grievance-pending-${g.id}`}>
                      <TableCell className="font-medium text-navy">{g.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{g.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600">{g.category}</TableCell>
                      <TableCell className="text-slate-600 text-sm min-w-[220px] max-w-[360px]"><span className="font-medium text-navy block">{g.subject}</span><span className="text-xs text-slate-500 whitespace-pre-line break-words block mt-0.5">{g.description}</span></TableCell>
                      <TableCell><StatusBadge status={g.priority === "Urgent" ? "Non-compliant" : g.priority === "High" ? "Notice" : "Active"} /></TableCell>
                      <TableCell className="text-slate-500 text-xs max-w-[180px]">{g.ro_decided_by ? <>Recommended by {g.ro_name || g.ro_decided_by}{g.ro_remarks ? ` — ${g.ro_remarks}` : ""}</> : <span className="text-slate-300">No reporting officer in chain</span>}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decide.mutate({ id: g.id, action: "approve" })} data-testid={`grievance-resolve-${g.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decide.mutate({ id: g.id, action: "reject" })} data-testid={`grievance-reject-${g.id}`}><XCircle className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          {(all || []).length === 0 ? (
            <EmptyState icon={MessageSquareWarning} title="No grievances yet" />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Category</TableHead>
                  <TableHead className="overline text-slate-500">Subject</TableHead>
                  <TableHead className="overline text-slate-500">Status</TableHead>
                  <TableHead className="overline text-slate-500">Final Remarks</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {all.map((g) => (
                    <TableRow key={g.id} className="table-row-i" data-testid={`grievance-all-${g.id}`}>
                      <TableCell className="font-medium text-navy">{g.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{g.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600">{g.category}</TableCell>
                      <TableCell className="text-slate-600 text-sm min-w-[200px] max-w-[340px] whitespace-normal break-words">{g.subject}</TableCell>
                      <TableCell><StatusBadge status={g.status} /></TableCell>
                      <TableCell className="text-slate-500 text-xs min-w-[200px] max-w-[340px] whitespace-normal break-words">{g.remarks || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
